import { ComponentProps, FC } from "react";
import * as SheetPrimitive from "@radix-ui/react-dialog";
import { XIcon } from "lucide-react";

import { useTranslate } from "@/hooks/useTranslate";
import { ChildrenOrI18nType } from "@/utils/react";
import { cn } from "@/utils/tailwind";

import { RmText } from "./RmText/RmText";
import { textVariants } from "./RmText/variants";

export const RmSheet: FC<
  ComponentProps<typeof SheetPrimitive.Root>
> = props => {
  return <SheetPrimitive.Root data-slot="sheet" {...props} />;
};

export const RmSheetTrigger: React.FC<
  ComponentProps<typeof SheetPrimitive.Trigger>
> = props => {
  return <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />;
};

export const RmSheetClose: FC<
  ComponentProps<typeof SheetPrimitive.Close>
> = props => {
  return <SheetPrimitive.Close data-slot="sheet-close" {...props} />;
};

export const RmSheetPortal: FC<
  ComponentProps<typeof SheetPrimitive.Portal>
> = props => {
  return <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;
};

export const RmSheetOverlay: FC<
  ComponentProps<typeof SheetPrimitive.Overlay>
> = ({ className, ...props }) => {
  return (
    <SheetPrimitive.Overlay
      data-slot="sheet-overlay"
      className={cn(
        "fixed inset-0 z-50 bg-black/80 backdrop-blur-md data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
};

type RmSheetContentPropsType = ComponentProps<typeof SheetPrimitive.Content> & {
  side?: "top" | "right" | "bottom" | "left";
  hideClose?: boolean;
};

export const RmSheetContent: FC<RmSheetContentPropsType> = ({
  className,
  children,
  side = "right",
  hideClose = false,
  onPointerDownOutside,
  ...props
}) => {
  return (
    <RmSheetPortal>
      <RmSheetOverlay>
        <SheetPrimitive.Content
          data-slot="sheet-content"
          className={cn(
            "fixed z-50 flex flex-col gap-4 overflow-y-auto bg-background px-6 pb-6 shadow-lg transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-500",
            side === "right" &&
              "inset-y-0 right-0 h-full w-full border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right lg:max-w-7/12",
            side === "left" &&
              "inset-y-0 left-0 h-full w-full border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left lg:max-w-7/12",
            side === "top" &&
              "inset-x-0 top-0 h-auto border-b data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
            side === "bottom" &&
              "inset-x-0 bottom-0 h-auto border-t data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
            className,
          )}
          onInteractOutside={e => {
            if (
              e.target instanceof Element &&
              e.target.closest(".pac-container")
            ) {
              e.preventDefault();
            }
          }}
          onPointerDownOutside={e => {
            if (
              e.target instanceof Element &&
              e.target.closest("[data-sonner-toast]")
            ) {
              e.preventDefault();
            }

            onPointerDownOutside?.(e);
          }}
          {...props}>
          {children}
          {!hideClose && (
            <SheetPrimitive.Close className="absolute top-4 right-4 cursor-pointer rounded-xs opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring/50 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none data-[state=open]:bg-secondary">
              <XIcon className="size-4" />
              <RmText as="span" className="sr-only" i18nKey="common:close" />
            </SheetPrimitive.Close>
          )}
        </SheetPrimitive.Content>
      </RmSheetOverlay>
    </RmSheetPortal>
  );
};

export const RmSheetHeader: FC<ComponentProps<"div">> = ({
  className,
  ...props
}) => {
  return (
    <div
      data-slot="sheet-header"
      className={cn("flex flex-col gap-1.5 pt-6", className)}
      {...props}
    />
  );
};

export const RmSheetFooter: FC<ComponentProps<"div">> = ({
  className,
  ...props
}) => {
  return (
    <div
      data-slot="sheet-footer"
      className={cn(
        "mt-auto flex flex-col-reverse justify-end gap-3 pt-4 md:flex-row",
        className,
      )}
      {...props}
    />
  );
};

type RmSheetTitlePropsType = ComponentProps<typeof SheetPrimitive.Title> &
  ChildrenOrI18nType;

export const RmSheetTitle: FC<RmSheetTitlePropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <SheetPrimitive.Title
      data-slot="sheet-title"
      className={cn(
        "text-foreground",
        textVariants({ variant: "headerTwo" }),
        className,
      )}
      {...props}>
      {children ?? i18nText}
    </SheetPrimitive.Title>
  );
};

type RmSheetDescriptionPropsType = ComponentProps<
  typeof SheetPrimitive.Description
> &
  ChildrenOrI18nType;

export const RmSheetDescription: FC<RmSheetDescriptionPropsType> = ({
  className,
  children,
  i18nKey,
  i18nOptions,
  ...props
}) => {
  const i18nText = useTranslate(i18nKey, i18nOptions);

  return (
    <SheetPrimitive.Description
      data-slot="sheet-description"
      className={cn("text-muted-foreground", textVariants(), className)}
      {...props}>
      {children ?? i18nText}
    </SheetPrimitive.Description>
  );
};
